import {
  differenceInYears,
  format,
  getHours,
  getMinutes,
  parse,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { dateTimeFormat } from '@/constants/date'

import { TimeZoneUTCType, TimeZoneType } from '@/enums'

/**
 * @function getIsoUTCFromDateTime
 * Convert date time to ISO UTC
 * @param date
 * @param time
 * @param timeZone
 * @returns Date in UTC format
 */
export const getIsoUTCFromDateTime = (date: Date, time: string) => {
  let d = date
  if (typeof d === 'string') d = new Date(date)
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  const dateTime = new Date(d.setUTCHours(hours, minutes, 0, 0))
  return dateTime.toISOString()
}

/**
 * @function getIsoLocalTimeFromDateTime
 * Convert date time to ISO Local Time
 * @param date
 * @param time
 * @param timeZone
 * @returns Date in UTC format
 */
export const getIsoLocalTimeFromDateTime = (
  date: Date,
  time: string,
  endTime?: boolean,
) => {
  let d = date
  if (typeof d === 'string') d = new Date(date)
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  const seconds = endTime ? 59 : 0
  const dateTime = new Date(d.setHours(hours, minutes, seconds, 0))

  // TODO: We could add a param to change with several local times
  const isoDateTime = new Date(
    dateTime.getTime() - dateTime.getTimezoneOffset() * 60000,
  ).toISOString()

  return isoDateTime
}

/**
 * @function getDateFromDateTime
 * Convert date time to ISO UTC
 * @param date
 * @param time
 * @param timeZone
 * @returns Date in UTC format
 */
export const getDateFromDateTime = (date: Date, time: string) => {
  let d = date
  if (typeof d === 'string' || d === null) d = new Date(date)
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  const dateTime = new Date(d.setHours(hours, minutes, 0, 0))
  return dateTime
}

/**
 * @function getDateTimeFromIsoUTC
 * Convert ISO UTC to date time
 * @param isoDate
 * @param timeZone
 * @returns Date Time
 */
export const getDateTimeFromIsoUTC = (
  isoDate: string,
  timeZone: TimeZoneUTCType,
) => {
  return utcToZonedTime(new Date(isoDate), timeZone)
}

/**
 * @function getTimeFromDate
 * Convert Date to Time (Hours / Minutes)
 * @param date
 * @returns Time (Hours / Minute)
 */
export const getTimeFromDate = (date: Date): string => {
  const hours = getHours(date)
  const minutes = getMinutes(date)

  const hoursFormat = hours < 10 ? `0${hours}` : hours
  const minutesFormat = minutes < 10 ? `0${minutes}` : minutes

  const time = `${hoursFormat}:${minutesFormat}:00`
  return time
}

/**
 * @function getRoundedTime
 * Convert time to rounded time
 * @param time
 * @returns Rounded Time
 */
export const getRoundedTime = (time: string): string => {
  const hours = Number(time.split(':')[0])
  let minutes = Number(time.split(':')[1])
  minutes = Math.ceil(minutes / 5) * 5
  const seconds = Number(time.split(':')[2]) || '00'

  const hoursFormat = hours < 10 ? `0${hours}` : hours
  const minutesFormat = minutes < 10 ? `0${minutes}` : minutes

  const timeRounded = `${hoursFormat}:${minutesFormat}:${seconds}`
  return timeRounded
}

/**
 * @function setTimeForToday
 * Set date time with a specific time
 * @param time
 * @returns Current Date
 */
export const setTimeForToday = (time: string): Date => {
  const hours = Number(time.split(':')[0])
  const minutes = Number(time.split(':')[1])
  const seconds = Number(time.split(':')[2])
  return new Date(new Date().setHours(hours, minutes, seconds))
}

/**
 * @function getTimeZoneFromUserInSessionStorage
 * Gets timezone from user present in session storage
 * @returns Timezone in GMT+00 format
 */
export const getTimeZoneFromUserInSessionStorage = () => {
  const userSession = sessionStorage.getItem('user')
  let timeZone = TimeZoneType.GMT_0
  if (userSession) {
    timeZone = JSON.parse(userSession).timezone
  }
  return timeZone
}

/**
 * @function getDateFromISOString
 * Gets date from user ISO String
 * @returns Date without ISO String
 */
export const getDateFromISOString = (date: string) => {
  const dateValue = date.split('T')[0]
  const [year, month, day] = dateValue.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/**
 * @function getISOStringFromDateString
 * Gets ISO String from Date keeping timezone offset
 * @returns ISO String
 */
export const getISOStringFromDateString = (dateStr: string) => {
  const date = new Date(dateStr)
  return new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  ).toISOString()
}

/**
 * @function getISOStringFromStartDate
 * Gets ISO String from Date - Start Date (T00:00:00)
 * @returns ISO String
 */
export const getISOStringFromStartDate = (date: Date) => {
  const year = date.getFullYear()
  const month =
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()
  return `${year}-${month}-${day}T00:00:00.000Z`
}

/**
 * @function getISOStringFromEndDate
 * Gets ISO String from Date - End Date (T23:59:59)
 * @returns ISO String
 */
export const getISOStringFromEndDate = (date: Date) => {
  const year = date.getFullYear()
  const month =
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()
  return `${year}-${month}-${day}T23:59:59.000Z`
}

/**
 * @function formatDate
 * This functions allows you gets the formatted date to GMT-00 and dd/MM/yyyy - H:mm:ss'
 * @param date
 * @returns Date Formatted
 */
export const formatDate = (date: string) => {
  return format(
    getDateTimeFromIsoUTC(
      date,
      TimeZoneUTCType['GMT+00 Coordinated Universal Time'],
    ),
    dateTimeFormat,
  )
}

/**
 * @function serverUTCDateTimeStringToLocalDate
 * Gets a string date time like this one '12/02/2022 00:00:00',
 * then appends UTC at the end of the date time and returns
 * the local date time based on the new UTC string created
 * @returns Locale date
 */
export const serverUTCDateTimeStringToLocalDate = (dateTimeStr: string) => {
  if (dateTimeStr.includes('T') || dateTimeStr.includes('Z')) {
    return new Date(dateTimeStr)
  }

  const newUTCDateTimeStr = `${dateTimeStr} UTC`
  return new Date(newUTCDateTimeStr)
}

/**
 * @function getTimezoneAbbreviationFromDate
 * Gets a date and returns the date's timezone abbreviation
 * @returns Date's timezone abbreviation string
 */
export const getTimezoneAbbreviationFromDate = (date: Date = new Date()) => {
  return date
    .toLocaleTimeString(undefined, { timeZoneName: 'short' })
    .split(' ')[2]
}

/**
 * @function convertFrom24To12Format
 * Gets a 24 hour format string and converts it to 12 hour format.
 * @returns String with time in a 12 hour format
 */
export const convertFrom24To12Format = (timeStr: string) => {
  return new Date(`1970-01-01T${timeStr}Z`).toLocaleTimeString('en-US', {
    timeZone: 'UTC',
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
  })
}

/**
 * @function localeDateToStringWithTimeZoneAbbreviation
 * Gets a local date.
 * @returns String date formatted including timezone abbreviation.
 */
export const localeDateToStringWithTimeZoneAbbreviation = (
  localDate: Date,
  timezone?: string,
  inputOptions?: Intl.DateTimeFormatOptions,
) => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }

  let options = defaultOptions
  if (inputOptions) {
    options = { ...options, ...inputOptions }
  }

  return `${localDate.toLocaleString('en-US', options)} ${timezone}`
}

/**
 * @function formatDate
 * This functions allows you gets the formatted date to Locale Date String'
 * @param date
 * @returns Date Formatted
 */
export const formatRowDate = (
  dateStr: string | undefined,
  timezone?: string,
) => {
  if (dateStr) {
    const date = serverUTCDateTimeStringToLocalDate(dateStr)
    return localeDateToStringWithTimeZoneAbbreviation(date, timezone || '')
  }
  return ''
}

/**
 * @function birthdayDate
 * @param fecha
 * @returns
 */
export function birthdayDate(date: string, yearsText: string) {
  const splitDate = date?.split('T') || ''

  const birthday = parse(splitDate[0], 'yyyy-MM-dd', new Date())

  const yearsOld = differenceInYears(new Date(), birthday)

  const formattedBirthday = format(birthday, 'dd/MM/yyyy')

  return `${formattedBirthday} - ${yearsOld} ${yearsText}`
}
